import { useState, useEffect } from "react";
import { useAppSelector } from "../../../hooks/redux";
import {
  selectItems,
} from "src/modules/spItems/selectors";
import { fieldType } from "src/types/form";
import _ from "lodash";
import Form from "src/components/Form";
import { addItemRequest, getItemsRequest } from "src/modules/spItems/api";
import { selectCurrentUser, selectUsers } from "src/modules/users/selector";
import {
  collectionsListName,
  departmentsListName,
  dependenciesListName,
  documentByCardFilesLibName,
  documentCodeIndexesListName,
  equipmentsByDocumentListName,
  equipmentsListName,
  linesListName,
  MDWorkstationListName,
  processesListName,
  sectorsListName,
  typesListName,
} from "src/api/config";
import { docStatuses } from "src/api/config/systemSwitch";

type Props = {
  documentCardID: number | null;
  formData: any;
  setFormData: any;
  setIdRemoveElem?: any;
  openCancelAddCard?: boolean;
  setOpenCancelAddCard?: (b: boolean) => void;
  setApproversCardDocumentData: any;
  versionView: number | null;
  setHideCloseIcon: (b: boolean) => void;
  showEditApprovers: any;
  setShowEditApprovers: any;
  sendApproveBlock: any;
  setSendApproveBlock: any;
  setRemoveDocument: any;
  removeDocument: any;
  card: any;
  setCard: any;
  setUpdateCardData: any;
  setImagesDoc: any;
  removeObjData: any;
  isMobileDocumentCard?: boolean;
};

type SelectOption = {
  id: number;
  name: string;
};

const getLookupId = (lookup: any): number | null => {
  const value = lookup?.get_lookupId?.() ?? lookup?.ID ?? lookup?.id;
  if (value === null || value === undefined) return null;

  const id = Number(value);
  return Number.isNaN(id) ? null : id;
};

const getLookupValue = (lookup: any): string | null => {
  const value =
    lookup?.get_lookupValue?.() ||
    lookup?.Title ||
    lookup?.title ||
    lookup?.Name ||
    lookup?.name;

  return value ? String(value) : null;
};

const getLookupOption = (lookup: any): SelectOption | null => {
  const id = getLookupId(lookup);
  const name = getLookupValue(lookup);

  return id !== null && name ? { id, name } : null;
};

const isSelectOption = (option: SelectOption | null): option is SelectOption => option !== null;

const getWorkplaceOption = (workplace: any): SelectOption | null => {
  const locationName = getLookupValue(workplace?.Location);
  const id = Number(workplace?.ID ?? workplace?.id);

  if (!locationName || Number.isNaN(id)) return null;

  return {
    id,
    name: `${locationName}_${workplace.Workstation || ""}`,
  };
};

const Card = (props: Props) => {
  const {
    documentCardID,
    formData,
    setFormData,
    setIdRemoveElem,
    openCancelAddCard,
    setOpenCancelAddCard,
    setApproversCardDocumentData,
    versionView,
    setHideCloseIcon,
    showEditApprovers,
    setShowEditApprovers,
    sendApproveBlock,
    setSendApproveBlock,
    setRemoveDocument,
    removeDocument,
    card,
    setCard,
    setUpdateCardData,
    setImagesDoc,
    removeObjData,
    isMobileDocumentCard = false,
  } = props;
  const [fieldsForm, setFieldsForm] = useState<fieldType[]>([
    {
      type: "file",
      key: "File",
      require: true,
      width: "w-full",
      title: "Перетащите документ сюда или",
      titleFile: "выберите файл",
      placeholder:
        "Размер файла не более 50MB. Eсли это видео, то не более 200MB",
    },
    {
      type: "input",
      key: "Title",
      require: true,
      title: "Название",
      placeholder: "Введите название",
      width: "w-full tablet:w-[calc(50%-10px)]",
    },
    {
      type: "select",
      require: true,
      key: "DocType",
      title: "Тип документа, соответствующий шаблону",
      placeholder: "Выберите из списка",
      width: "w-full tablet:w-[calc(50%-10px)]",
    },
    {
      type: "select",
      require: true,
      key: "Collection",
      title: "Коллекция",
      placeholder: "Выберите из списка",
      width: "w-full tablet:w-[calc(50%-10px)]",
    },
    {
      type: "select",
      require: true,
      title: "Подразделение/Функция",
      key: "Department",
      placeholder: "Не выбрано",
      width: "w-full tablet:w-[calc(50%-10px)]",
    },
    {
      type: "select",
      require: true,
      title: "Колонна/Направление/Отдел",
      key: "Sector",
      placeholder: "Выберите из списка",
      width: "w-full tablet:w-[calc(50%-10px)]",
    },
    {
      type: "select",
      require: true,
      key: "Process",
      title: "Блок колонны/Участок",
      placeholder: "Не выбрано",
      width: "w-full tablet:w-[calc(50%-10px)]",
    },
    {
      type: "select",
      require: false,
      multiple: true,
      title: "Рабочее место",
      key: "WorkPlaces",
      notice: "Изменение рабочих мест не требует согласования",
      placeholder: "Выберите из списка",
      width: "w-full",
    },
    {
      type: "select",
      require: true,
      title: "Критичность",
      key: "Critical",
      notice: "Изменение критичности не требует согласования",
      placeholder: "Выберите из списка",
      width: "w-full",
    },
    {
      type: "peoplePicker",
      require: true,
      single: true,
      key: "ResponsibleForRevision",
      title: "Ответственный за пересмотр",
      current: false,
      notice: "Изменение ответственного за пересмотр не требует согласования",
      placeholder: "Введите имя или фамилию",
      width: "w-full tablet:w-[calc(50%-10px)]",
    },
    {
      type: "date",
      require: true,
      key: "DateForRevision",
      title: "Срок пересмотра",
      placeholder: "Выберите дату",
      notice: "Изменение срока пересмотра не требует согласования",
      width: "w-full tablet:w-[calc(50%-10px)]",
    },
    {
      type: "repeater",
      key: equipmentsListName,
      title: "Оборудование",
      fields: [
        {
          type: "autocomplete",
          require: false,
          title: "SAP № технического места",
          key: "SAP",
          placeholder: "Начните вводить SAP",
          width: "w-[calc(50%-10px)]",
          searchApiField: "SAP",
          searchApiList: equipmentsListName,
          searchApiSelectFields:
            "ID,Title,SAP,Manufacturer,Model,TypeEquipment",
          searchApiFilterField: "",
          searchApiFilterFieldValue: "",
        },
        {
          type: "select",
          require: false,
          key: "Line",
          title: "Линия розлива",
          placeholder: "Выберите из списка",
          width: "w-[calc(50%-10px)]",
        },
        {
          type: "text",
          title: "Название оборудования",
          key: "Title",
          value: "",
          require: false,
          width: "w-full",
        },
        {
          type: "text",
          title: "Производитель оборудования",
          key: "Manufacturer",
          value: "",
          require: false,
          width: "w-full",
        },
        {
          type: "text",
          title: "Модель оборудования",
          key: "Model",
          value: "",
          require: false,
          width: "w-full",
        },
        {
          type: "text",
          title: "Тип оборудования",
          key: "TypeEquipment",
          value: "",
          require: false,
          width: "w-full",
        },
      ],
      width: "w-full",
      require: false,
    },
    {
      type: "input",
      require: false,
      key: "Code",
      disabled: true,
      title: "Кодировка документа",
      placeholder: "",
      width: "w-full",
    },
    {
      type: "input",
      require: false,
      disabled: true,
      title: "Папка",
      key: "Path",
      placeholder:
        "Коллекция/Подразделение.Функция/Колонна.Направление.Отдел/Блок колонны.Участок/Тип документа",
      width: "w-full",
    },
  ]);
  const [dependenceFields, setDependenceFields] = useState<any>({
    Collection: null,
    Department: null,
    Process: null,
    Sector: null,
    Type: null,
  });
  const [dependencies, setDependencies] = useState<any>([]);
  const [workplaces, setWorkplaces] = useState<any>([]);
  const types: any = useAppSelector((state) => selectItems(state, typesListName));
  const sectors: any = useAppSelector((state) => selectItems(state, sectorsListName));
  const processes: any = useAppSelector((state) => selectItems(state, processesListName));
  const departments: any = useAppSelector((state) => selectItems(state, departmentsListName));
  const collections: any = useAppSelector((state) => selectItems(state, collectionsListName));
  const lines: any = useAppSelector((state) => selectItems(state, linesListName));
  const [code, setCode] = useState("__.__.__.__.__");
  const [defaultValues, setDefaultValues] = useState<any>({});
  const [indexDocument, setIndexDocument] = useState("");
  const users: any = useAppSelector(selectUsers);
  const currentUser: any = useAppSelector(selectCurrentUser);
  const [currentUserRole, setCurrentUserRole] = useState<any>({});
  const [checkLoadUsers, setCheckLoadUsers] = useState(false);

  //Расставляем опции селектов коллекции, типа и критичности
  useEffect(() => {
    if (currentUser.id) {
      const tempFieldsForm = [...fieldsForm];
      for (let i = 0; i < tempFieldsForm.length; i++) {
        if (tempFieldsForm[i]["key"] === "DocType") {
          tempFieldsForm[i]["options"] = types.map((type: any) => {
            return {
              id: type.ID,
              name: type.Title,
            };
          });
        }
        if (tempFieldsForm[i]["key"] === "Collection") {
          const options = collections.filter((collection: any) => {
            if (
              currentUser.isAdmin ||
              (currentUser.isCollectionAdmin &&
                currentUser.isCollectionAdmin.find(
                  (collectionID: any) => collectionID === collection.ID
                )) ||
              (currentUser.isCollectionEditor &&
                currentUser.isCollectionEditor.length > 0 &&
                currentUser.isCollectionEditor.find(
                  (collectionID: any) => collectionID === collection.ID
                ))
            ) {
              return true
            } else {
              return false
            }
          }).map((collection: any) => {
            return {
              id: collection.ID,
              name: collection.Title,
              available: true,
            };
          });
          tempFieldsForm[i]["options"] = _.uniqWith(options, _.isEqual);
        }
        if (tempFieldsForm[i]["key"] === "Critical") {
          tempFieldsForm[i]["options"] = [
            {
              id: 1,
              name: "Критичный",
            },
            {
              id: 0,
              name: "Не критичный",
            },
          ];
        }
      }
      setFieldsForm([...tempFieldsForm]);
    }
  }, [types, collections, currentUser]);

  useEffect(() => {
    if (dependenceFields["Collection"]) {
      getItemsRequest({ listName: dependenciesListName, filters: { Collection: dependenceFields["Collection"]["id"] }, sortOrder: { name: "SortDepend", asc: true } }).then((result) => {
        setDependencies([...result.items]);
      });
    }
    if (!dependenceFields["Collection"]) {
      const tempFieldsForm = [...fieldsForm];
      for (let i = 0; i < tempFieldsForm.length; i++) {
        if (
          tempFieldsForm[i]["key"] === "Department" ||
          tempFieldsForm[i]["key"] === "Sector" ||
          tempFieldsForm[i]["key"] === "Process" ||
          tempFieldsForm[i]["key"] === "WorkPlaces"
        ) {
          tempFieldsForm[i]["disabled"] = true;
        }
      }
      setFieldsForm([...tempFieldsForm]);
    }
    if (dependenceFields["Collection"]) {
      const tempFieldsForm = [...fieldsForm];
      for (let i = 0; i < tempFieldsForm.length; i++) {
        if (tempFieldsForm[i]["key"] === "Department" || tempFieldsForm[i]["key"] === "WorkPlaces") {
          tempFieldsForm[i]["disabled"] = false;
        }
        if (
          tempFieldsForm[i]["key"] === "Sector" ||
          tempFieldsForm[i]["key"] === "Process"
        ) {
          tempFieldsForm[i]["disabled"] = true;
        }
      }
      setFieldsForm([...tempFieldsForm]);
    }
    if (dependenceFields["Department"]) {
      const tempFieldsForm = [...fieldsForm];
      for (let i = 0; i < tempFieldsForm.length; i++) {
        if (tempFieldsForm[i]["key"] === "Sector") {
          tempFieldsForm[i]["disabled"] = false;
        }
        if (tempFieldsForm[i]["key"] === "Process") {
          tempFieldsForm[i]["disabled"] = true;
        }
      }
      setFieldsForm([...tempFieldsForm]);
    }
    if (dependenceFields["Sector"]) {
      const tempFieldsForm = [...fieldsForm];
      for (let i = 0; i < tempFieldsForm.length; i++) {
        if (tempFieldsForm[i]["key"] === "Process") {
          tempFieldsForm[i]["disabled"] = false;
        }
      }
      setFieldsForm([...tempFieldsForm]);
    }
  }, [JSON.stringify(dependenceFields)]);

  // Оределяем кодировку по дефолту и обновляем ее
  useEffect(() => {
    setCode(
      `${(dependenceFields["Collection"] &&
        collections.length > 0 &&
        collections.filter(
          (collection: any) =>
            collection.ID === dependenceFields["Collection"]["id"]
        ).length > 0 &&
        collections.filter(
          (collection: any) =>
            collection.ID === dependenceFields["Collection"]["id"]
        )[0]["Code"]) ||
      "__"
      }.${(dependenceFields["Department"] &&
        departments.length > 0 &&
        departments.filter(
          (department: any) =>
            department.ID === dependenceFields["Department"]["id"]
        ).length > 0 &&
        departments.filter(
          (department: any) =>
            department.ID === dependenceFields["Department"]["id"]
        )[0]["Code"]) ||
      "__"
      }.${(dependenceFields["Sector"] &&
        sectors.length > 0 &&
        sectors.filter(
          (sector: any) => sector.ID === dependenceFields["Sector"]["id"]
        ).length > 0 &&
        sectors.filter(
          (sector: any) => sector.ID === dependenceFields["Sector"]["id"]
        )[0]["Code"]) ||
      "__"
      }.${(dependenceFields["Process"] &&
        processes.length > 0 &&
        processes.filter(
          (process: any) => process.ID === dependenceFields["Process"]["id"]
        ).length > 0 &&
        processes.filter(
          (process: any) => process.ID === dependenceFields["Process"]["id"]
        )[0]["Code"]) ||
      "__"
      }.${(dependenceFields["Type"] && dependenceFields["Type"]["name"]) || "__"
      }${(indexDocument && "." + indexDocument) || ""}`
    );
  }, [dependenceFields, indexDocument]);

  //Отслеживаем изменение селектов после выбора коллекции (зависимые значения)
  useEffect(() => {
    const tempFieldsForm = [...fieldsForm];
    for (let i = 0; i < tempFieldsForm.length; i++) {
      if (dependenceFields["Collection"]) {
        if (tempFieldsForm[i]["key"] === "Department") {
          const options = dependencies
            .filter(
              (dependence: any) =>
                getLookupId(dependence.Collection) ===
                dependenceFields["Collection"]["id"]
            )
            .map((dependence: any) => getLookupOption(dependence.Department))
            .filter(isSelectOption);
          tempFieldsForm[i]["options"] = _.uniqWith(options, _.isEqual);
        }
        if (tempFieldsForm[i]["key"] === equipmentsListName) {
          tempFieldsForm[i]["fields"]?.map((field) => {
            if (field["key"] === "Line") {
              const options = lines
                .filter(
                  (line: any) =>
                    getLookupId(line.Collection) === dependenceFields["Collection"]["id"]
                )
                .map((line: any) => {
                  return {
                    id: line.ID,
                    name: line.Title,
                  };
                });
              field["options"] = _.uniqWith(options, _.isEqual);
            }
            if (field["key"] === "SAP") {
              field["searchApiFilterField"] = "Collection";
              field["searchApiFilterFieldValue"] =
                dependenceFields["Collection"]["name"];
            }
          });
        }
      }
      if (dependenceFields["Department"]) {
        if (tempFieldsForm[i]["key"] === "Sector") {
          const options = dependencies
            .filter(
              (dependence: any) =>
                getLookupId(dependence.Department) ===
                dependenceFields["Department"]["id"]
            )
            .map((dependence: any) => getLookupOption(dependence.Sector))
            .filter(isSelectOption);
          tempFieldsForm[i]["options"] = _.uniqWith(options, _.isEqual);
        }
      }
      if (dependenceFields["Sector"]) {
        if (tempFieldsForm[i]["key"] === "Process") {
          const options = dependencies
            .filter(
              (dependence: any) =>
                getLookupId(dependence.Sector) === dependenceFields["Sector"]["id"] &&
                getLookupId(dependence.Department) ===
                dependenceFields["Department"]["id"]
            )
            .map((dependence: any) => getLookupOption(dependence.Process))
            .filter(isSelectOption);
          tempFieldsForm[i]["options"] = _.uniqWith(options, _.isEqual);
        }
      }
      if (dependenceFields["Collection"] && tempFieldsForm[i]["key"] === "WorkPlaces") {
        const options =
          (workplaces.length > 0 &&
            workplaces
              .map(getWorkplaceOption)
              .filter(isSelectOption)
              .filter((place) => place.name.indexOf(dependenceFields["Collection"]['name']) !== -1)
              .sort((a, b) => a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }))) ||
          [];
        tempFieldsForm[i]["options"] = _.uniqWith(options, _.isEqual);
      }
    }
    setFieldsForm([...tempFieldsForm]);
  }, [dependencies, workplaces]);

  //Готовим и ставим значение карточки по умолчанию
  useEffect(() => {
    if (
      card &&
      Object.keys(users).length > 0 &&
      !checkLoadUsers &&
      workplaces.length > 0
    ) {
      const tempDefaultValues: any = {};
      Object.keys(card).map((key: string) => {
        const field = card[key];
        if (key === "ResponsibleForRevision") {
          tempDefaultValues[key] = [
            {
              label:
                (users[field.get_email()] && users[field.get_email()].displayName) ||
                field.get_lookupValue(),
              value: field.get_email(),
              avatar: (users[field.Email] && users[field.Email].avatar) || null,
              email: field.get_email()
            },
          ];
          if (users[field.get_email()]) {
            setCheckLoadUsers(true);
          }
        } else if (key === "DateForRevision") {
          tempDefaultValues[key] = {
            startDate: field,
            endDate: field,
          };
        } else if (
          key === "Collection" ||
          key === "DocType" ||
          key === "Department" ||
          key === "Process" ||
          key === "Sector" ||
          key === "Status"
        ) {
          tempDefaultValues[key] = {
            id: field.get_lookupId(),
            name: field.get_lookupValue(),
          };
        } else if (key === "WorkPlaces") {
          tempDefaultValues[key] =
            (field &&
              Array.isArray(field) &&
              field.length > 0 &&
              field
                .map((item) => {
                  const workplace = workplaces.find(w => w.ID === item.get_lookupId());
                  if (!workplace) return null;
                  return getWorkplaceOption(workplace);
                })
                .filter(isSelectOption)
                .sort((a, b) => a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }))) ||
            [];
        } else if (key === "Author") {
          return false;
        } else if (key === equipmentsListName) {
          return false;
        } else if (key === "Critical") {
          if (field) {
            tempDefaultValues[key] = {
              id: 1,
              name: "Критичный",
            };
          } else {
            tempDefaultValues[key] = {
              id: 0,
              name: "Не критичный",
            };
          }
        } else {
          tempDefaultValues[key] = field;
        }
      });
      setDefaultValues((prevState: any) => {
        return { ...prevState, ...tempDefaultValues };
      });
    }
  }, [JSON.stringify(card), users, workplaces, checkLoadUsers]);

  // Определяем доступность карточки для пользователя
  useEffect(() => {
    if (
      card &&
      currentUser.id &&
      Object.keys(currentUserRole).length > 0 &&
      currentUserRole.hasOwnProperty("replaced")
    ) {
      if (
        !currentUserRole.author &&
        !currentUserRole.replaced &&
        ((versionView &&
          (card.Status.get_lookupId() === docStatuses.public ||
            card.Status.get_lookupId() === docStatuses.review)) ||
          (!currentUser.isAdmin &&
            !currentUser.isCollectionEditor &&
            !currentUser.isCollectionAdmin) ||
          (!currentUser.isAdmin &&
            !currentUser.isCollectionAdmin &&
            currentUser.isCollectionEditor &&
            !currentUser.isCollectionEditor.some(
              (collection: any) => collection === card.Collection.get_lookupId()
            )) ||
          (!currentUser.isAdmin &&
            !currentUser.isCollectionEditor &&
            currentUser.isCollectionAdmin &&
            !currentUser.isCollectionAdmin.some(
              (collection: any) => collection === card.Collection.get_lookupId()
            )) ||
          (!currentUser.isAdmin &&
            currentUser.isCollectionAdmin &&
            currentUser.isCollectionEditor &&
            !currentUser.isCollectionEditor.some(
              (collection: any) => collection === card.Collection.get_lookupId()
            ) &&
            !currentUser.isCollectionAdmin.some(
              (collection: any) => collection === card.Collection.get_lookupId()
            )))
      ) {
        // Если просмотр согласованной, не актуально версии
        const tempFieldsForm = [...fieldsForm];
        for (let i = 0; i < tempFieldsForm.length; i++) {
          tempFieldsForm[i]["disabled"] = true;
          if (tempFieldsForm[i]["type"] === "repeater") {
            const fields = tempFieldsForm[i]["fields"] || [];
            for (let k = 0; k < fields.length; k++) {
              fields[k]["disabled"] = true;
            }
            tempFieldsForm[i]["fields"] = [...fields];
          }
        }
        setFieldsForm([...tempFieldsForm]);
      } else {
        const tempFieldsForm = [...fieldsForm];
        for (let i = 0; i < tempFieldsForm.length; i++) {
          if (
            tempFieldsForm[i]["key"] === "Code" ||
            tempFieldsForm[i]["key"] === "Path"
          ) {
            tempFieldsForm[i]["disabled"] = true;
          } else {
            tempFieldsForm[i]["disabled"] = false;
          }
          if (tempFieldsForm[i]["type"] === "repeater") {
            const fields = tempFieldsForm[i]["fields"] || [];
            for (let k = 0; k < fields.length; k++) {
              fields[k]["disabled"] = false;
            }
            tempFieldsForm[i]["fields"] = [...fields];
          }
        }
        setFieldsForm([...tempFieldsForm]);
      }
    }
  }, [card, dependenceFields, versionView, currentUser, currentUserRole]);

  useEffect(() => {
    if (card) {
      getItemsRequest({
        listName: MDWorkstationListName,
        viewFields: ['Location', 'ID', 'Workstation', 'Title'],
        populateFields: ['Location']
      }).then((result) => {
        setWorkplaces([...result.items]);
      });
      getItemsRequest({ listName: documentByCardFilesLibName, filters: { CardID: versionView || documentCardID, ForHistory: false, VersionDocument: versionView && true || false }, sortOrder: { name: 'Modified', desc: true } }).then((results) => {
        if (results.items.length === 0) {
          getItemsRequest({ listName: documentByCardFilesLibName, filters: { CardID: versionView || documentCardID, ForHistory: true, VersionDocument: versionView && true || false }, sortOrder: { name: 'Modified', desc: true } }).then((results) => {
            if (results.items.length === 0) {
              setDefaultValues((prevState: any) => {
                return {
                  ...prevState,
                  File: [],
                };
              });
            } else {
              setDefaultValues((prevState: any) => {
                return {
                  ...prevState,
                  File: [results.items[0]],
                };
              });
            }
          });
        } else {
          setDefaultValues((prevState: any) => {
            return {
              ...prevState,
              File: [results.items[0]],
            };
          });
        }
      });
      //Определяем оборудование по дефолту
      getItemsRequest({ listName: equipmentsByDocumentListName, filters: { Document: versionView || documentCardID, VersionDocument: versionView && true || false } }).then((results) => {
        console.log('results.items_equipmentsByDocumentListName: ', results.items);
        if (results.items.length > 0) {
          let tempDefaultValues: any = [];
          results.items.forEach(item => {
            if (item.Equipment && item.Line) {
              tempDefaultValues = [...tempDefaultValues, {
                SAP: {
                  id: item.Equipment.get_lookupId(),
                  name: item.Equipment.get_metadata().SAP,
                  ID: item.Equipment.get_lookupId(),
                  Manufacturer: item.Equipment.get_metadata().Manufacturer,
                  Model: item.Equipment.get_metadata().Model,
                  TypeEquipment: item.Equipment.get_metadata().TypeEquipment,
                  Title: item.Equipment.get_metadata().Title,
                },
                Line: item.Line && {
                  id: item.Line.get_lookupId(),
                  name: item.Line.get_lookupValue(),
                },
                ID: item.ID,
              }]
            }
            if (!item.Equipment && item.Line) {
              tempDefaultValues = [...tempDefaultValues, {
                SAP: null,
                Line: item.Line && {
                  id: item.Line.get_lookupId(),
                  name: item.Line.get_lookupValue(),
                },
                ID: item.ID,
              }]
            }
            if (item.Equipment && !item.Line) {
              tempDefaultValues = [...tempDefaultValues, {
                SAP: {
                  id: item.Equipment.get_lookupId(),
                  name: item.Equipment.get_metadata().SAP,
                  ID: item.Equipment.get_lookupId(),
                  Manufacturer: item.Equipment.get_metadata().Manufacturer,
                  Model: item.Equipment.get_metadata().Model,
                  TypeEquipment: item.Equipment.get_metadata().TypeEquipment,
                  Title: item.Equipment.get_metadata().Title,
                },
                Line: null,
                ID: item.ID,
              }]
            }
          });
          setDefaultValues((prevState: any) => {
            return { ...prevState, Equipments: [...tempDefaultValues] };
          });
        } else {
          setDefaultValues((prevState: any) => {
            return { ...prevState, Equipments: [] };
          });
        }
      });
    }
  }, [JSON.stringify(card)]);

  //Определяем индекс документа
  useEffect(() => {
    if (
      card &&
      code.indexOf("__") === -1 &&
      card.Code.indexOf("__") === -1 &&
      code.indexOf(card.Code) !== -1
    ) {
      setIndexDocument(card.Index);
    }
    if (
      card &&
      code.indexOf("__") === -1 &&
      card.Code.indexOf("__") === -1 &&
      code.indexOf(card.Code) === -1
    ) {
      const codeArr = code.split(".");
      let codeRequest = "";
      if (codeArr.length === 6) {
        codeArr.forEach((item: string, idx: number) => {
          if (idx <= 3) {
            codeRequest += `${item}.`;
          }
          if (idx === 4) {
            codeRequest += `${item}`;
          }
        });
      } else {
        codeRequest = code;
      }
      getItemsRequest({
        listName: documentCodeIndexesListName,
        filters: { Title: codeRequest },
        rowLimit: 1,
        sortOrder: { name: "Index", desc: true },
      }).then((results) => {
        let ans = "";
        let indexNumber = 0;
        if (results.items.length === 0) {
          indexNumber = results.items.length + 1;
          ans = ("0000" + String(results.items.length + 1)).substring(
            String(results.items.length + 1).length
          );
          setIndexDocument(ans);
        } else {
          indexNumber = results.items[0]["Index"] + 1;
          ans = ("0000" + String(results.items[0]["Index"] + 1)).substring(
            String(results.items[0]["Index"] + 1).length
          );
          setIndexDocument(ans);
        }
        if (Object.keys(formData).length > 0 && ans && indexNumber) {
          let code = "",
            index = "";
          const codeArr = formData.Code.split(".");
          index = codeArr[codeArr.length - 1];
          code = formData.Code.slice(0, -(index.length + 1));
          addItemRequest({
            listName: documentCodeIndexesListName,
            body: {
              Title: code,
              Index: index,
            },
            updateReducer: false,
          });
        }
      });
    }
  }, [code, formData, card]);

  return (
    <>
      <div className="card_document">
        <Form
          setIdRemoveElem={setIdRemoveElem}
          code={code}
          setDependenceFields={setDependenceFields}
          type="card"
          setFormData={setFormData}
          fieldsForm={fieldsForm}
          documentCardID={documentCardID}
          defaultValues={defaultValues}
          setOpenCancelAddCard={setOpenCancelAddCard}
          openCancelAddCard={openCancelAddCard}
          setApproversCardDocumentData={setApproversCardDocumentData}
          versionView={versionView}
          setHideCloseIcon={setHideCloseIcon}
          showEditApprovers={showEditApprovers}
          setShowEditApprovers={setShowEditApprovers}
          sendApproveBlock={sendApproveBlock}
          setSendApproveBlock={setSendApproveBlock}
          setRemoveDocument={setRemoveDocument}
          removeDocument={removeDocument}
          currentUserRole={currentUserRole}
          setCurrentUserRole={setCurrentUserRole}
          setUpdateCardData={setUpdateCardData}
          setCard={setCard}
          card={card}
          setImagesDoc={setImagesDoc}
          setCheckLoadUsers={setCheckLoadUsers}
          removeObjData={removeObjData}
          isMobileDocumentCard={isMobileDocumentCard}
        />
      </div>
    </>
  );
};

export default Card;
